import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OfflineQueueService } from '@/offline/offline-queue.service';
import { SyncService } from '@/offline/sync.service';
import { ConnectivityService } from '@/offline/connectivity.service';
import { WriteQueueEntry } from '@/offline/db';

const ENTITY_LABELS: Record<string, string> = {
    documents: 'Documento',
    tickets: 'Ticket',
    purchases: 'Compra',
    clients: 'Cliente',
    products: 'Producto',
    categories: 'Categoría',
    measurements: 'Medida',
    providers: 'Proveedor',
    pay_methods: 'Método de pago',
    roles: 'Rol',
};

const STATUS_LABELS: Record<WriteQueueEntry['status'], string> = {
    pending: 'Pendiente',
    syncing: 'Sincronizando…',
    synced: 'Sincronizado',
    failed: 'Error',
};

const ACTION_LABELS: Record<WriteQueueEntry['method'], string> = {
    POST: 'Crear',
    PUT: 'Actualizar',
    DELETE: 'Eliminar',
};

@Component({
    selector: 'app-sync-status-list',
    imports: [CommonModule],
    templateUrl: './sync-status-list.page.html',
})
export class SyncStatusListPage implements OnInit, OnDestroy {

    private queue = inject(OfflineQueueService);
    private sync = inject(SyncService);
    private connectivity = inject(ConnectivityService);

    entries = signal<WriteQueueEntry[]>([]);
    loading = signal(true);
    /** Real backend reachability (see ConnectivityService), not just navigator.onLine. */
    online = this.connectivity.backendOnline;

    entityLabels = ENTITY_LABELS;
    statusLabels = STATUS_LABELS;
    actionLabels = ACTION_LABELS;

    private refreshHandle: ReturnType<typeof setInterval> | null = null;

    ngOnInit(): void {
        this.refresh();
        this.refreshHandle = setInterval(() => this.refresh(), 3000);
    }

    ngOnDestroy(): void {
        if (this.refreshHandle) clearInterval(this.refreshHandle);
    }

    async refresh(): Promise<void> {
        this.entries.set(await this.queue.list());
        this.loading.set(false);
    }

    describeEntry(entry: WriteQueueEntry): string {
        return this.entityLabels[entry.entityType] ?? entry.entityType;
    }

    async retry(entry: WriteQueueEntry): Promise<void> {
        if (entry.id == null) return;
        await this.queue.update({ ...entry, status: 'pending', lastError: null });
        await this.refresh();
        this.sync.replay();
    }

    async discard(entry: WriteQueueEntry): Promise<void> {
        if (entry.id == null) return;
        await this.queue.remove(entry.id);
        await this.refresh();
    }

    async retryAll(): Promise<void> {
        this.sync.replay();
    }
}
